import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/avatar'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { fmtDate, fmtMoney } from '@/features/shared/format'
import type { OdemeView } from '../../api'
import { MobileList } from '../MobileList'
import { OdemeStatusPill } from '../StatusPill'
import { OdemeRowActions } from './OdemeRowActions'

export function OdemeTable({
  rows,
  selectedId,
  onSelect,
}: {
  rows: OdemeView[]
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  const { t } = useTranslation()
  const date = (d: string) => (d === '—' ? '—' : fmtDate(d))

  return (
    <Card className="overflow-hidden rounded-2xl border-border/70 shadow-sm">
      <CardContent className="px-0 pt-0 pb-0">
        {rows.length === 0 ? (
          <div className="px-5 py-14 text-center text-sm text-muted-foreground">{t('hr.personel.empty')}</div>
        ) : (
          <>
            <MobileList
              className="px-2"
              rows={rows.map((o) => ({
                key: o.id,
                leading: <Avatar name={o.ad} className="size-9" />,
                title: o.ad,
                subtitle: `${t(`hr.personel.odeme.tur.${o.tur}`)} · ${o.donem}`,
                trailing: <OdemeStatusPill durum={o.durum} />,
                meta: (
                  <>
                    <span className="font-medium tabular-nums text-foreground">{fmtMoney(o.tutar)}</span>
                    <span>·</span>
                    <span>{date(o.tarih)}</span>
                  </>
                ),
                selected: selectedId === o.id,
                onClick: () => onSelect(o.id),
              }))}
            />
            <Table className="hr-table hidden md:table">
              <THead>
                <TR>
                  <TH>{t('hr.personel.columns.personel')}</TH>
                  <TH>{t('hr.personel.odemeGecmis.tur')}</TH>
                  <TH>{t('hr.personel.ucret.donem')}</TH>
                  <TH className="text-right">{t('hr.personel.odemeGecmis.tutar')}</TH>
                  <TH>{t('hr.personel.odemeGecmis.tarih')}</TH>
                  <TH>{t('hr.personel.columns.durum')}</TH>
                  <TH className="w-10" />
                </TR>
              </THead>
              <TBody>
                {rows.map((o) => (
                  <TR key={o.id} data-selected={selectedId === o.id ? 'true' : undefined} className="cursor-pointer" onClick={() => onSelect(o.id)}>
                    <TD>
                      <div className="flex items-center gap-3">
                        <Avatar name={o.ad} />
                        <div className="min-w-0">
                          <div className="truncate font-medium">{o.ad}</div>
                          <div className="truncate text-xs text-muted-foreground">
                            {o.unvan} · {o.birim}
                          </div>
                        </div>
                      </div>
                    </TD>
                    <TD>{t(`hr.personel.odeme.tur.${o.tur}`)}</TD>
                    <TD>{o.donem}</TD>
                    <TD className="text-right font-semibold tabular-nums">{fmtMoney(o.tutar)}</TD>
                    <TD className="text-muted-foreground">{date(o.tarih)}</TD>
                    <TD>
                      <OdemeStatusPill durum={o.durum} />
                    </TD>
                    <TD>
                      <OdemeRowActions odeme={o} />
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  )
}
